import { defineStore } from 'pinia'
import { useColors } from 'vuestic-ui'
import { getThemeColors, getThemeShadows } from '@/config/theme-colors'

export const useThemeStore = defineStore('theme', {
  state: () => {
    // Initialize system theme
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

    return {
      theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
      systemTheme: systemTheme as 'light' | 'dark',
    }
  },

  actions: {
    // Initialize system theme detection
    initSystemTheme() {
      const updateSystemTheme = () => {
        this.systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      }
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', updateSystemTheme)
    },

    // Apply custom theme CSS variables
    applyCustomTheme(themeType: 'light' | 'dark') {
      const root = document.documentElement

      // Mark theme as initialized after first load to enable transitions
      if (!root.hasAttribute('data-theme-initialized')) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          root.setAttribute('data-theme-initialized', 'true')
        })
      }

      root.setAttribute('data-theme', themeType)

      // Get colors from centralized theme configuration
      const colors = getThemeColors(themeType)
      const shadows = getThemeShadows(themeType)

      const selectedTheme = {
        primaryColor: colors.primary,
        primaryDarkColor: colors.primaryDark,
        secondaryColor: colors.secondary,
        accentColor: colors.accent,
        backgroundPrimary: colors.backgroundPrimary,
        backgroundSecondary: colors.backgroundSecondary,
        backgroundTertiary: colors.backgroundTertiary,
        backgroundCardPrimary: colors.backgroundCardPrimary,
        backgroundCardSecondary: colors.backgroundCardSecondary,
        backgroundElement: colors.backgroundElement,
        backgroundBorder: colors.backgroundBorder,
        textPrimary: colors.textPrimary,
        textSecondary: colors.textSecondary,
        textTertiary: colors.textTertiary,
        textInverted: colors.textInverted,
        successColor: colors.successColor,
        dangerColor: colors.dangerColor,
        warningColor: colors.warningColor,
        infoColor: colors.info,
        statusActiveBg: colors.statusActiveBg,
        statusWarningBg: colors.statusWarningBg,
        statusErrorBg: colors.statusErrorBg,
        statusSuccessBg: colors.statusSuccessBg || colors.statusActiveBg,
        borderColor: colors.borderColor,
        cardBorder: colors.cardBorder,
        dividerColor: colors.dividerColor || colors.borderColor,
        headerBg: colors.headerBg,
        navbarBg: (colors as { navbarBg?: string }).navbarBg ?? colors.primary,
        navbarBgBottom: (colors as { navbarBgBottom?: string }).navbarBgBottom ?? colors.primaryDark,
        navbarBorderColor: (colors as { navbarBorderColor?: string }).navbarBorderColor ?? colors.borderColor,
        navbarBorderWidth: (colors as { navbarBorderWidth?: string }).navbarBorderWidth ?? '1px',
        navbarTextColor: (colors as { navbarTextColor?: string }).navbarTextColor ?? colors.textPrimary,
        tabBg: colors.tabBg,
        tabActiveBg: colors.tabActiveBg,
        themeSurface: colors.themeSurface,
        themeSurfaceVariant: colors.themeSurfaceVariant,
        themeToggleBg: colors.themeToggleBg,
        shadow: shadows.md,
        shadowSm: shadows.sm,
        shadowMd: shadows.md,
        shadowLg: shadows.xl,
        shadowInner: shadows.inner || 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
      }

      // Apply all CSS variables
      root.style.setProperty('--va-primary', selectedTheme.primaryColor)
      root.style.setProperty('--va-primary-dark', selectedTheme.primaryDarkColor)
      root.style.setProperty('--va-secondary', selectedTheme.secondaryColor)
      root.style.setProperty('--va-accent', selectedTheme.accentColor)
      root.style.setProperty('--va-background-primary', selectedTheme.backgroundPrimary)
      root.style.setProperty('--va-background-secondary', selectedTheme.backgroundSecondary)
      root.style.setProperty('--va-background-tertiary', selectedTheme.backgroundTertiary)
      root.style.setProperty('--va-background-card-primary', selectedTheme.backgroundCardPrimary)
      root.style.setProperty('--va-background-card-secondary', selectedTheme.backgroundCardSecondary)
      root.style.setProperty('--va-background-element', selectedTheme.backgroundElement)
      root.style.setProperty('--va-background-border', selectedTheme.backgroundBorder)
      root.style.setProperty('--va-text-primary', selectedTheme.textPrimary)
      root.style.setProperty('--va-text-secondary', selectedTheme.textSecondary)
      root.style.setProperty('--va-text-tertiary', selectedTheme.textTertiary)
      root.style.setProperty('--va-text-inverted', selectedTheme.textInverted)
      root.style.setProperty('--va-success', selectedTheme.successColor)
      root.style.setProperty('--va-danger', selectedTheme.dangerColor)
      root.style.setProperty('--va-warning', selectedTheme.warningColor)
      root.style.setProperty('--va-info', selectedTheme.infoColor)
      root.style.setProperty('--va-status-active-bg', selectedTheme.statusActiveBg)
      root.style.setProperty('--va-status-warning-bg', selectedTheme.statusWarningBg)
      root.style.setProperty('--va-status-error-bg', selectedTheme.statusErrorBg)
      root.style.setProperty('--va-status-success-bg', selectedTheme.statusSuccessBg)
      root.style.setProperty('--va-border-color', selectedTheme.borderColor)
      root.style.setProperty('--va-card-border', selectedTheme.cardBorder)
      root.style.setProperty('--va-divider-color', selectedTheme.dividerColor)
      root.style.setProperty('--va-header-bg', selectedTheme.headerBg)
      root.style.setProperty('--va-navbar-bg', selectedTheme.navbarBg)
      root.style.setProperty('--va-navbar-bg-bottom', selectedTheme.navbarBgBottom)
      root.style.setProperty('--va-navbar-border-color', selectedTheme.navbarBorderColor)
      root.style.setProperty('--va-navbar-border-width', selectedTheme.navbarBorderWidth)
      root.style.setProperty('--va-navbar-text-color', selectedTheme.navbarTextColor)
      root.style.setProperty('--va-tab-bg', selectedTheme.tabBg)
      root.style.setProperty('--va-tab-active-bg', selectedTheme.tabActiveBg)
      root.style.setProperty('--va-theme-surface', selectedTheme.themeSurface)
      root.style.setProperty('--va-theme-surface-variant', selectedTheme.themeSurfaceVariant)
      root.style.setProperty('--va-theme-toggle-bg', selectedTheme.themeToggleBg)
      root.style.setProperty('--va-shadow', selectedTheme.shadow)
      root.style.setProperty('--va-shadow-sm', selectedTheme.shadowSm)
      root.style.setProperty('--va-shadow-md', selectedTheme.shadowMd)
      root.style.setProperty('--va-shadow-lg', selectedTheme.shadowLg)
      root.style.setProperty('--va-shadow-inner', selectedTheme.shadowInner)
    },

    // Set theme function
    setTheme(newTheme: 'light' | 'dark') {
      this.theme = newTheme
      localStorage.setItem('theme', newTheme)

      // Try to apply vuestic preset
      try {
        const colors = useColors()
        if (colors?.applyPreset) {
          colors.applyPreset(newTheme)
        }
      } catch (e) {
        console.warn('Could not apply vuestic preset:', e)
      }

      // Apply custom CSS variables
      this.applyCustomTheme(newTheme)
    },
  },
})
